import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  Book,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { PageHeader } from 'components/ui/page-header';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { TableSearch } from 'components/ui/data-table';
import { TablePager } from 'components/ui/pagination';
import { PERMISSIONS } from 'modules/auth/permissions';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { cn } from 'lib/utils';
import { formatDateShort, formatGroupedNumber } from 'lib/dateFormat';
import { usePermission } from 'modules/auth/hooks';
import { useIsMobile } from 'hooks/use-media-query';
import { SubjectsMobile } from 'components/mobile/screens/SubjectsMobile';
import type { Subject } from 'modules/subject/types';
import {
  useSubjectList,
  useCreateSubject,
  useUpdateSubject,
  useDeleteSubject,
} from 'modules/subject';
import { SUBJECT_COLORS } from 'modules/subject/colors';

interface SubjectFormState {
  name: string;
  description: string;
  color: string;
}

const defaultForm = (): SubjectFormState => ({
  name: '',
  description: '',
  color: SUBJECT_COLORS[0],
});

type SortField = 'name' | 'questionCount' | 'createdAt';
type SortOrder = 'asc' | 'desc';

interface SortHeaderProps {
  field: SortField;
  label: string;
  active: SortField;
  order: SortOrder;
  onSort: (field: SortField) => void;
}

/** Column label that sorts on click; only the leading column shows its arrow (others on hover or focus). */
function SortHeader({ field, label, active, order, onSort }: SortHeaderProps) {
  const isActive = active === field;
  const Icon = !isActive ? ArrowUpDown : order === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      // Arbitrary variant, not group-hover: group-* variants emit no CSS here (docs/architecture/overview.md).
      className="inline-flex items-center gap-1.5 font-medium transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&:focus-visible>svg]:opacity-60 [&:hover>svg]:opacity-60"
    >
      <span>{label}</span>
      <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-foreground' : 'opacity-0')} aria-hidden />
    </button>
  );
}

const LoadingRows: React.FC = () => (
  <div className="divide-y divide-border" aria-hidden>
    {[0, 1, 2, 3, 4].map((row) => (
      <div key={row} className="flex items-center gap-4 px-4 py-4">
        <Skeleton className="h-2.5 w-2.5" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className={cn('h-3.5', row % 2 ? 'w-32' : 'w-44')} />
          <Skeleton className="h-3 w-56" />
        </div>
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="hidden h-3.5 w-24 md:block" />
      </div>
    ))}
  </div>
);

const SubjectsDesktop: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: subjects = [], isLoading } = useSubjectList();
  const createSubject = useCreateSubject();
  const updateSubject = useUpdateSubject();
  const deleteSubject = useDeleteSubject();

  const canCreate = usePermission(PERMISSIONS.SUBJECTS_CREATE);
  const canEdit = usePermission(PERMISSIONS.SUBJECTS_EDIT);
  const canDelete = usePermission(PERMISSIONS.SUBJECTS_DELETE);
  const canDeleteQuestions = usePermission(PERMISSIONS.QUESTIONS_DELETE);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Subject | null>(null);
  // The target outlives the dialog so its content stays stable during the exit animation.
  const [deleteTarget, setDeleteTarget] = useState<Subject | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState<SubjectFormState>(defaultForm());

  // Search, sort, and pagination state
  const { input: search, setInput: setSearch } = useSearchParamState();
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const { page, pageSize, setPage, setPageSize } = usePaginationParams({
    defaultPage: 1,
    defaultPageSize: 10,
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredAndSortedSubjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = subjects;
    if (q) {
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          (s.description && s.description.toLowerCase().includes(q))
      );
    }
    return [...result].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortField === 'questionCount') {
        cmp = (a.questionCount ?? 0) - (b.questionCount ?? 0);
      } else if (sortField === 'createdAt') {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        cmp = timeA - timeB;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [subjects, search, sortField, sortOrder]);

  const totalFiltered = filteredAndSortedSubjects.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / pageSize));

  // Adjust page if out of bounds after deletion or page size change
  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount, setPage]);

  const pagedSubjects = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAndSortedSubjects.slice(start, start + pageSize);
  }, [filteredAndSortedSubjects, page, pageSize]);

  const openCreate = () => {
    setEditTarget(null);
    setForm(defaultForm());
    setDialogOpen(true);
  };

  const openEdit = (subject: Subject) => {
    setEditTarget(subject);
    setForm({ name: subject.name, description: subject.description ?? '', color: subject.color ?? SUBJECT_COLORS[0] });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    if (editTarget) {
      await updateSubject.mutateAsync({ id: editTarget.id, data: form });
    } else {
      await createSubject.mutateAsync(form);
    }
    setDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSubject.mutateAsync(deleteTarget.id);
    } catch {
      // useDeleteSubject already shows the error toast.
    }
  };

  const isMutating = createSubject.isPending || updateSubject.isPending;
  const deleteQuestionCount = deleteTarget?.questionCount ?? 0;

  const ariaSort = (field: SortField): 'ascending' | 'descending' | undefined =>
    sortField === field ? (sortOrder === 'asc' ? 'ascending' : 'descending') : undefined;

  // The bar compares each subject with the largest one.
  const maxQuestions = Math.max(1, ...subjects.map((s) => s.questionCount ?? 0));
  const totalQuestions = subjects.reduce((sum, s) => sum + (s.questionCount ?? 0), 0);

  const renderBody = () => {
    if (isLoading) return <LoadingRows />;
    if (subjects.length === 0) {
      return (
        <EmptyState
          icon={<Book size={28} />}
          title={t('subjects_empty')}
          action={canCreate ? { label: t('new_subject'), onClick: openCreate } : undefined}
        />
      );
    }
    if (totalFiltered === 0) {
      return (
        <EmptyState
          icon={<Search size={28} />}
          title={t('no_subjects_match', 'No subjects match your search')}
          description={t('try_adjusting_your_search_or_filters', 'Try searching for a different keyword or clear your search')}
          action={{ label: t('clear_search', 'Clear search'), onClick: () => setSearch('') }}
        />
      );
    }
    return (
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead aria-sort={ariaSort('name')}>
              <SortHeader field="name" label={t('subject')} active={sortField} order={sortOrder} onSort={handleSort} />
            </TableHead>
            <TableHead className="w-44 lg:w-64" aria-sort={ariaSort('questionCount')}>
              <SortHeader
                field="questionCount"
                label={t('questions')}
                active={sortField}
                order={sortOrder}
                onSort={handleSort}
              />
            </TableHead>
            <TableHead className="hidden w-40 md:table-cell" aria-sort={ariaSort('createdAt')}>
              <SortHeader field="createdAt" label={t('created_at')} active={sortField} order={sortOrder} onSort={handleSort} />
            </TableHead>
            <TableHead className="w-32 text-right">
              <span className="sr-only">{t('actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pagedSubjects.map((subject) => (
            <TableRow
              key={subject.id}
              tabIndex={0}
              role="button"
              aria-label={t('subject_open_aria', {
                name: subject.name,
                defaultValue: 'Open {{name}} in Question Bank',
              })}
              onClick={() => navigate(`/app/questions?subjectId=${subject.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/app/questions?subjectId=${subject.id}`);
                }
              }}
              data-tour={subject.isDemo ? 'demo-subject-card' : undefined}
              className="h-[60px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <TableCell className="max-w-0 py-2.5">
                <div className="flex min-w-0 items-center gap-3">
                  {/* The subject's own colour, as a small key rather than a tinted tile. */}
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                    style={{ backgroundColor: subject.color ?? '#64748b' }}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium leading-5 text-foreground [.group:hover_&]:underline">
                        {subject.name}
                      </span>
                      {subject.isDemo && (
                        <Badge variant="outline" className="border-border text-[10px] font-normal text-muted-foreground">
                          {t('tour.badge_sample', 'Namuna')}
                        </Badge>
                      )}
                    </div>
                    <p
                      className={cn(
                        'mt-0.5 truncate text-[12.5px] leading-[18px]',
                        subject.description ? 'text-muted-foreground' : 'text-muted-foreground/60',
                      )}
                    >
                      {subject.description || t('library.no_description')}
                    </p>
                  </div>
                </div>
              </TableCell>

              <TableCell>
                <div className="flex items-center gap-3">
                  <span className="w-10 text-right text-[13.5px] font-medium tabular-nums text-foreground">
                    {subject.questionCount}
                  </span>
                  <span aria-hidden className="hidden h-1 flex-1 overflow-hidden rounded-full bg-muted lg:block">
                    <span
                      className="block h-full rounded-full bg-foreground"
                      style={{ width: `${Math.round(((subject.questionCount ?? 0) / maxQuestions) * 100)}%` }}
                    />
                  </span>
                </div>
              </TableCell>

              <TableCell className="hidden text-[13px] tabular-nums text-muted-foreground md:table-cell">
                {formatDateShort(subject.createdAt)}
              </TableCell>

              <TableCell className="py-0 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-0.5">
                  {canEdit && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      title={t('edit_subject', 'Edit subject')}
                      aria-label={t('edit_subject_aria', {
                        name: subject.name,
                        defaultValue: 'Edit {{name}}',
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(subject);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      title={t('delete', 'Delete')}
                      aria-label={t('delete_subject_aria', {
                        name: subject.name,
                        defaultValue: 'Delete {{name}}',
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(subject);
                        setDeleteOpen(true);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    title={t('view_questions', 'View questions')}
                    aria-label={t('view_questions', 'View questions')}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/app/questions?subjectId=${subject.id}`);
                    }}
                  >
                    <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            {t('subjects')}
            {!isLoading && subjects.length > 0 && (
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                {subjects.length}
              </span>
            )}
          </span>
        }
        description={t('subjects_subtitle')}
        actions={
          canCreate && (
            <Button size="sm" className="gap-1.5" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('new_subject')}
            </Button>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <TableSearch
          value={search}
          onChange={setSearch}
          placeholder={t('subjects_search_placeholder', 'Search subjects by name or description...')}
          aria-label={t('subjects_search_placeholder', 'Search subjects by name or description...')}
          className="w-full sm:w-80"
        />
        {!isLoading && subjects.length > 0 && (
          <span className="ml-auto whitespace-nowrap text-[13px] tabular-nums text-muted-foreground">
            <span className="font-semibold text-foreground">{formatGroupedNumber(totalQuestions)}</span>{' '}
            {t('pager_noun_questions')} · {t('n_subjects', { count: subjects.length })}
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {renderBody()}
        {!isLoading && totalFiltered > 0 && (
          <div className="border-t border-border px-4 py-3">
            <TablePager
              page={page}
              pageCount={pageCount}
              pageSize={pageSize}
              total={totalFiltered}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              noun={t('pager_noun_subjects')}
            />
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editTarget ? t('edit_subject') : t('new_subject')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">{t('name')} *</label>
              <Input
                placeholder={t('subject_name_placeholder')}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t('description')}</label>
              <Input
                placeholder={t('subject_description_placeholder')}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('color')}</label>
              <div className="flex flex-wrap gap-2">
                {SUBJECT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    aria-label={t('select_color_aria', { color, defaultValue: 'Select color {{color}}' })}
                    aria-pressed={form.color === color}
                    className={cn(
                      'w-7 h-7 rounded-full border-2 transition-transform hover:scale-110',
                      form.color === color ? 'border-foreground scale-110' : 'border-transparent',
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setForm((f) => ({ ...f, color }))}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={!form.name.trim() || isMutating}>
              {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editTarget ? t('save_changes') : t('create_subject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('subject_delete_title', { name: deleteTarget?.name ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteQuestionCount === 0
                ? t('trash.subject_delete_empty')
                : canDeleteQuestions
                  ? t('subject_delete_has_questions', { count: deleteQuestionCount })
                  : t('subject_delete_no_question_permission')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            {deleteQuestionCount > 0 && !canDeleteQuestions ? (
              // Deleting a subject also deletes its questions, which needs the
              // questions-delete permission. Without it, point at the Question
              // Bank filtered by this subject instead of dead-ending on Cancel.
              <AlertDialogAction
                onClick={() => {
                  setDeleteOpen(false);
                  if (deleteTarget) navigate(`/app/questions?subjectId=${deleteTarget.id}`);
                }}
              >
                {t('subject_delete_view_questions', 'View questions')}
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteQuestionCount > 0
                  ? t('subject_delete_with_questions', { count: deleteQuestionCount })
                  : t('delete')}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Phones render the dedicated mobile subject cards; thin wrapper keeps hook
// count constant across the breakpoint.
export const SubjectsPage: React.FC = () => (useIsMobile() ? <SubjectsMobile /> : <SubjectsDesktop />);
